import { WebViewerInstance } from '@pdftron/webviewer';
import { MessageInstance } from 'antd/es/message/interface';
import { Dispatch, SetStateAction } from 'react';

import { ViewerAppryzeNavigationTool } from './viewer-appryze-navigation-tool';

type DrawingToolBarOptions = {
    onPinDrop?: (payload: { pageNumber: number; x: number; y: number }) => void;
    onOpenFilters?: () => void;
};

export const getToolBarForDrawings =
    (
        dispatch: any,
        setLoading: Dispatch<SetStateAction<boolean>>,
        sheetData: any,
        messageApi: MessageInstance,
        options?: DrawingToolBarOptions,
    ) =>
    (instance: WebViewerInstance) => {
        const { UI } = instance;
        const { Core } = instance;

        const zoomControls = new UI.Components.Zoom({});

        const pinToolName = 'AnnotationCreatePinMarker';
        const pinSvg = `<svg fill="#222222" width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2c-3.86 0-7 3.14-7 7 0 5.12 5.42 10.35 6.63 11.46.21.2.49.3.77.3.28 0 .56-.1.77-.3 1.21-1.11 6.63-6.34 6.63-11.46 0-3.86-3.14-7-7-7zm0 16.04C10.11 16.14 7 12.46 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 3.46-3.11 7.14-5 9.04z"></path>
            <circle cx="12" cy="9" r="2.5"></circle>
        </svg>`;

        let isNavigationToolActive = false;
        let isPinToolActive = false;
        let isPinToolArmed = false;
        let ignorePinDropUntil = 0;

        const isElement = (node: unknown): node is Element =>
            Boolean(node && typeof node === 'object' && (node as Element).nodeType === 1);
        const pinInteractiveSelector =
            '[data-pin-interactive="true"], [data-modal-interactive="true"], .ant-modal, .ant-modal-wrap, .ant-modal-root, [role="dialog"]';
        const isInteractiveElement = (element: Element | null) => Boolean(element?.closest(pinInteractiveSelector));
        const markIgnorePinDrop = () => {
            ignorePinDropUntil = Date.now() + 250;
        };
        const consumeIgnorePinDrop = () => {
            if (!ignorePinDropUntil) {
                return false;
            }
            const now = Date.now();
            if (now <= ignorePinDropUntil) {
                ignorePinDropUntil = 0;
                return true;
            }
            ignorePinDropUntil = 0;
            return false;
        };
        const collectEventElements = (nativeEvent: any) => {
            const elements: Element[] = [];
            const pushElement = (node: unknown) => {
                if (isElement(node)) {
                    elements.push(node);
                }
            };
            if (!nativeEvent) {
                return elements;
            }
            pushElement(nativeEvent.target);
            const path = typeof nativeEvent.composedPath === 'function' ? nativeEvent.composedPath() : [];
            path.forEach(pushElement);
            if (Number.isFinite(nativeEvent.clientX) && Number.isFinite(nativeEvent.clientY)) {
                const doc = nativeEvent.view?.document ?? window.document;
                const elementAtPoint = doc.elementFromPoint(nativeEvent.clientX, nativeEvent.clientY);
                pushElement(elementAtPoint);
            }
            return elements;
        };
        const isInteractiveEventTarget = (event: Event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (isInteractiveElement(target)) {
                return true;
            }
            const path = typeof (event as Event).composedPath === 'function' ? event.composedPath() : [];
            return path.some((node) => isElement(node) && isInteractiveElement(node));
        };
        const registerPinDropGuard = () => {
            const guardKey = '__smxPinDropGuardAttached__';
            if ((instance as any)[guardKey]) {
                return;
            }
            (instance as any)[guardKey] = true;
            const viewerElement = Core.documentViewer.getViewerElement?.() ?? null;
            const docs = new Set<Document>();
            if (viewerElement?.ownerDocument) {
                docs.add(viewerElement.ownerDocument);
            }
            const iframeWindow = (instance.UI as { iframeWindow?: Window }).iframeWindow;
            if (iframeWindow?.document) {
                docs.add(iframeWindow.document);
            }
            const iframe = viewerElement?.querySelector?.('iframe');
            if (iframe instanceof HTMLIFrameElement && iframe.contentDocument) {
                docs.add(iframe.contentDocument);
            }
            if (typeof document !== 'undefined') {
                docs.add(document);
            }
            const handlePointerDown = (event: PointerEvent) => {
                if (typeof event.button === 'number' && event.button !== 0) {
                    return;
                }
                if (isInteractiveEventTarget(event)) {
                    markIgnorePinDrop();
                }
            };
            docs.forEach((doc) => doc.addEventListener('pointerdown', handlePointerDown, true));
        };
        const isInteractiveAtViewerPoint = (toolEvent: any, viewerElement: Element | null) => {
            if (!viewerElement || !toolEvent) {
                return false;
            }
            const getViewerCoords = Core.documentViewer.getViewerCoordinatesFromMouseEvent;
            if (typeof getViewerCoords !== 'function') {
                return false;
            }
            const coords = getViewerCoords.call(Core.documentViewer, toolEvent);
            if (!coords || !Number.isFinite(coords.x) || !Number.isFinite(coords.y)) {
                return false;
            }
            const rect = viewerElement.getBoundingClientRect?.();
            if (!rect || !Number.isFinite(rect.left) || !Number.isFinite(rect.top)) {
                return false;
            }
            const doc = viewerElement.ownerDocument ?? window.document;
            const elementAtPoint = doc.elementFromPoint(rect.left + coords.x, rect.top + coords.y);
            return isInteractiveElement(elementAtPoint);
        };
        const shouldIgnorePinDrop = (nativeEvent: any, viewerElement: Element | null, toolEvent?: any) => {
            if (consumeIgnorePinDrop()) {
                return true;
            }
            const eventElements = collectEventElements(nativeEvent);
            if (eventElements.length > 0) {
                if (viewerElement && !eventElements.some((element) => isTargetInViewer(element, viewerElement))) {
                    return true;
                }
                if (eventElements.some((element) => isInteractiveElement(element))) {
                    return true;
                }
            }
            return Boolean(toolEvent && viewerElement && isInteractiveAtViewerPoint(toolEvent, viewerElement));
        };

        const clearPinTool = () => {
            const documentViewer = instance.Core.documentViewer;
            const pinTool = documentViewer.getTool(pinToolName);
            if (documentViewer.getToolMode() === pinTool) {
                documentViewer.setToolMode(documentViewer.getTool('Pan'));
            }
            isPinToolActive = false;
            isPinToolArmed = false;
            instance.UI.updateElement('pinToolButton', { isActive: false });
        };
        const isTargetInViewer = (target: Element | null, viewerElement: Element | null) => {
            if (!target || !viewerElement) {
                return true;
            }
            if (viewerElement.contains(target)) {
                return true;
            }
            const ownerDoc = target.ownerDocument;
            const frameEl = ownerDoc?.defaultView?.frameElement;
            return Boolean(frameEl && viewerElement.contains(frameEl));
        };

        const navigationButton = new instance.UI.Components.CustomButton({
            dataElement: 'navigationButton',
            title: 'Navigate to page',
            onClick: () => {
                const documentViewer = instance.Core.documentViewer;
                const navigationTool = documentViewer.getTool('AnnotationCreateScreenshot');
                const currentTool = documentViewer.getToolMode();

                if (isNavigationToolActive || currentTool === navigationTool) {
                    // Deactivate tool - return to Pan tool
                    documentViewer.setToolMode(documentViewer.getTool('Pan'));
                    isNavigationToolActive = false;
                    // Remove visual indication of pressed button
                    instance.UI.updateElement('navigationButton', { isActive: false });
                } else {
                    // Activate navigation tool
                    documentViewer.setToolMode(navigationTool);
                    isNavigationToolActive = true;
                    // Add visual indication of pressed button
                    instance.UI.updateElement('navigationButton', { isActive: true });
                }
            },
            img: 'icon-header-pan',
        });

        // Listen to tool changes to synchronize button state
        const resolveToolName = (tool: any) =>
            typeof tool === 'string'
                ? tool
                : tool?.name || tool?.toolName || tool?.ToolName || tool?.getToolName?.() || null;

        instance.Core.documentViewer.addEventListener('toolModeUpdated', (tool) => {
            const navigationToolName = 'AnnotationCreateScreenshot';
            const navigationTool = instance.Core.documentViewer.getTool(navigationToolName);
            const activeToolName = resolveToolName(tool);
            if (tool !== navigationTool && activeToolName !== navigationToolName && isNavigationToolActive) {
                isNavigationToolActive = false;
                instance.UI.updateElement('navigationButton', { isActive: false });
            } else if ((tool === navigationTool || activeToolName === navigationToolName) && !isNavigationToolActive) {
                isNavigationToolActive = true;
                instance.UI.updateElement('navigationButton', { isActive: true });
            }

            const pinTool = instance.Core.documentViewer.getTool(pinToolName);
            const isPinTool = tool === pinTool || activeToolName === pinToolName;
            if (!isPinTool && isPinToolActive) {
                isPinToolActive = false;
                isPinToolArmed = false;
                instance.UI.updateElement('pinToolButton', { isActive: false });
            } else if (isPinTool && !isPinToolActive) {
                if (!isPinToolArmed) {
                    clearPinTool();
                    return;
                }
                isPinToolActive = true;
                instance.UI.updateElement('pinToolButton', { isActive: true });
            }
        });

        instance.UI.addEventListener(instance.UI.Events.TOOLBAR_GROUP_CHANGED, () => {
            const activeRibbon = instance.UI.getActiveRibbonItem?.();
            if (activeRibbon !== 'smx-tool' && (isPinToolActive || isPinToolArmed)) {
                clearPinTool();
            }
        });

        const registerPinTool = () => {
            let existingTool: any = null;
            try {
                existingTool = Core.documentViewer.getTool(pinToolName);
            } catch {
                existingTool = null;
            }
            if (existingTool) {
                return;
            }

            class PinCreateTool extends Core.Tools.GenericAnnotationCreateTool {
                constructor(documentViewer: any) {
                    const DummyAnnotation = class extends Core.Annotations.RectangleAnnotation {
                        constructor() {
                            super();
                            this.Subject = 'PinMarker';
                        }
                    };
                    super(documentViewer, DummyAnnotation as any);
                    this.cursor = 'crosshair';
                }

                mouseLeftDown(e: any) {
                    const nativeEvent =
                        e?.event ?? e?.originalEvent ?? e?.nativeEvent ?? e?.detail ?? e ?? null;
                    const viewerElement = Core.documentViewer.getViewerElement?.() ?? null;
                    if (shouldIgnorePinDrop(nativeEvent, viewerElement, e)) {
                        return;
                    }
                    const existing = Core.annotationManager.getAnnotationByMouseEvent(e);
                    if (existing) {
                        return;
                    }
                    if (!isPinToolArmed) {
                        return;
                    }
                    let pdfPoint = Core.documentViewer.getPDFCoordinatesFromMouseEvent(e);
                    const fallbackPage =
                        typeof Core.documentViewer.getCurrentPage === 'function'
                            ? Core.documentViewer.getCurrentPage()
                            : 1;
                    let pageNumber =
                        pdfPoint?.pageNumber && Number.isFinite(pdfPoint.pageNumber)
                            ? pdfPoint.pageNumber
                            : fallbackPage;
                    if (!pdfPoint || !Number.isFinite(pdfPoint.x) || !Number.isFinite(pdfPoint.y)) {
                        const coords = Core.documentViewer.getViewerCoordinatesFromMouseEvent(e);
                        const displayMode = Core.documentViewer.getDisplayModeManager().getDisplayMode();
                        const pagePoint = displayMode.windowToPage(coords, fallbackPage);
                        if (!pagePoint || !Number.isFinite(pagePoint.x) || !Number.isFinite(pagePoint.y)) {
                            return;
                        }
                        pageNumber = pagePoint?.pageNumber ?? fallbackPage;
                        pdfPoint = {
                            x: pagePoint?.x ?? coords.x,
                            y: pagePoint?.y ?? coords.y,
                            pageNumber,
                        } as typeof pdfPoint;
                    }
                    const pageInfo = Core.documentViewer.getDocument()?.getPageInfo?.(pageNumber);
                    if (
                        pageInfo &&
                        Number.isFinite(pageInfo.width) &&
                        Number.isFinite(pageInfo.height) &&
                        (pdfPoint.x < 0 ||
                            pdfPoint.y < 0 ||
                            pdfPoint.x > pageInfo.width ||
                            pdfPoint.y > pageInfo.height)
                    ) {
                        return;
                    }
                    let normalizedY = pdfPoint.y;
                    try {
                        const pageHeight = pageInfo?.height;
                        if (Number.isFinite(pageHeight)) {
                            normalizedY = pageHeight - pdfPoint.y;
                        }
                    } catch {
                        // keep normalizedY as-is
                    }
                    const pinOffsetY = -5;
                    options?.onPinDrop?.({
                        pageNumber,
                        x: pdfPoint.x,
                        y: normalizedY - pinOffsetY,
                    });
                    // After placing a pin, immediately deactivate the pin tool.
                    clearPinTool();
                }
            }

            const pinTool = new PinCreateTool(Core.documentViewer);
            try {
                (pinTool as any).name = pinToolName;
                (pinTool as any).toolName = pinToolName;
                (pinTool as any).ToolName = pinToolName;
            } catch {
                // ignore
            }
            UI.registerTool({
                toolName: pinToolName,
                toolObject: pinTool as any,
                buttonImage: pinSvg,
                buttonName: 'pinToolButton',
                tooltip: 'Pin tool',
            });
        };

        registerPinTool();
        registerPinDropGuard();

        const pinToolButton = new UI.Components.CustomButton({
            dataElement: 'pinToolButton',
            title: 'Pin tool',
            onClick: () => {
                const documentViewer = instance.Core.documentViewer;
                const pinTool = documentViewer.getTool(pinToolName);
                const currentTool = documentViewer.getToolMode();

                if (isPinToolActive || currentTool === pinTool) {
                    clearPinTool();
                } else {
                    isPinToolArmed = true;
                    documentViewer.setToolMode(pinTool);
                    isPinToolActive = true;
                    instance.UI.updateElement('pinToolButton', { isActive: true });
                }
            },
            img: pinSvg,
        });

        const textToolsGroupedItems = new instance.UI.Components.GroupedItems({
            dataElement: 'group-smx-tool',
            items: [navigationButton, pinToolButton],
        });

        const ribbonItemSmxTool = new instance.UI.Components.RibbonItem({
            dataElement: 'smx-tool',
            label: 'SMX tool',
            // img: 'icon-arrow-down',
            // @ts-expect-error ignore
            groupedItems: ['group-smx-tool'],
        });

        const ribbonGroupSmxTool = new UI.Components.RibbonGroup({
            items: [ribbonItemSmxTool],
        });

        const menuButton = new instance.UI.Components.ToggleElementButton({
            dataElement: 'menuButton',
            img: 'ic-hamburger-menu',
            title: 'component.menuOverlay',
            toggleElement: 'MainMenuFlyout',
            // @ts-expect-error ignore
            type: 'toggleButton',
        });

        const defaultHeader = instance.UI.getModularHeader('default-top-header');
        defaultHeader.setItems([menuButton, zoomControls, ribbonGroupSmxTool]);

        const toolsHeader = instance.UI.getModularHeader('tools-header');
        // toolsHeader.setItems([textToolsGroupedItems]);
        toolsHeader.setItems([...toolsHeader.getItems(), textToolsGroupedItems]);

        ViewerAppryzeNavigationTool(instance, dispatch, setLoading, sheetData, (errorText) => {
            messageApi.open({
                type: 'warning',
                content: errorText,
            });
        });
    };
